import type { Alert, InfrastructureResource, MiddlewareInstance, MiddlewareResource, OperationAudit, PageResult, ResourceKind, ResourceRelation, Service, ServiceInstance } from "../domain/models";
import { alerts, audits, dependencies, infrastructureResources, instances, middlewareInstances, middlewareResources, resourceRelations, services } from "../mocks/data";

export interface ServiceQuery {
  keyword?: string;
  status?: string;
  environment?: string;
  owner?: string;
  page?: number;
  pageSize?: number;
}

export interface ResourceQuery {
  keyword?: string;
  type?: string;
  status?: string;
}

export interface OpsApi {
  listServices(query?: ServiceQuery): Promise<PageResult<Service>>;
  getService(id: string): Promise<Service>;
  listInstances(serviceId?: string): Promise<ServiceInstance[]>;
  listAlerts(): Promise<Alert[]>;
  listMiddlewareResources(query?: ResourceQuery): Promise<MiddlewareResource[]>;
  getMiddlewareResource(id: string): Promise<{ resource: MiddlewareResource; instances: MiddlewareInstance[]; relations: ResourceRelation[] }>;
  listInfrastructureResources(query?: ResourceQuery): Promise<InfrastructureResource[]>;
  getInfrastructureResource(id: string): Promise<{ resource: InfrastructureResource; relations: ResourceRelation[] }>;
  listResourceRelations(kind?: ResourceKind, resourceId?: string): Promise<ResourceRelation[]>;
  acknowledgeAlert(id: string, note: string): Promise<Alert>;
  executeInstanceAction(instanceId: string, action: "restart" | "online" | "offline", reason: string): Promise<OperationAudit>;
}

const wait = (ms = 260) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * First-party mock adapter. Replace this export with an HTTP adapter implementing
 * the same interface when Spring Boot and external observability systems are ready.
 */
export const mockOpsApi: OpsApi = {
  async listServices(query = {}) {
    await wait();
    const keyword = query.keyword?.trim().toLowerCase();
    const filtered = services.filter((service) => {
      const matchKeyword = !keyword || [service.name, service.code, service.owner, ...service.tags].some((value) => value.toLowerCase().includes(keyword));
      return matchKeyword && (!query.status || service.status === query.status) && (!query.environment || service.environment === query.environment) && (!query.owner || service.owner === query.owner);
    });
    const page = query.page ?? 1;
    const pageSize = query.pageSize ?? 10;
    return { items: filtered.slice((page - 1) * pageSize, page * pageSize), total: filtered.length, page, pageSize };
  },
  async getService(id) {
    await wait(160);
    const service = services.find((item) => item.id === id);
    if (!service) throw new Error("服务不存在或已被移除");
    return service;
  },
  async listInstances(serviceId) {
    await wait();
    return serviceId ? instances.filter((item) => item.serviceId === serviceId) : instances;
  },
  async listAlerts() {
    await wait();
    return alerts;
  },
  async listMiddlewareResources(query = {}) {
    await wait(220);
    const keyword = query.keyword?.trim().toLowerCase();
    return middlewareResources.filter((resource) => {
      const matchKeyword = !keyword || [resource.name, resource.type, resource.owner, resource.endpoint, ...resource.tags].some((value) => value.toLowerCase().includes(keyword));
      return matchKeyword && (!query.type || resource.type === query.type) && (!query.status || resource.status === query.status);
    });
  },
  async getMiddlewareResource(id) {
    await wait(180);
    const resource = middlewareResources.find((item) => item.id === id);
    if (!resource) throw new Error("中间件资源不存在或已被移除");
    return { resource, instances: middlewareInstances.filter((item) => item.middlewareId === id), relations: resourceRelations.filter((item) => (item.sourceKind === "middleware" && item.sourceId === id) || (item.targetKind === "middleware" && item.targetId === id)) };
  },
  async listInfrastructureResources(query = {}) {
    await wait(220);
    const keyword = query.keyword?.trim().toLowerCase();
    return infrastructureResources.filter((resource) => {
      const matchKeyword = !keyword || [resource.name, resource.type, resource.address, resource.region, resource.zone, ...resource.tags].some((value) => value.toLowerCase().includes(keyword));
      return matchKeyword && (!query.type || resource.type === query.type) && (!query.status || resource.status === query.status);
    });
  },
  async getInfrastructureResource(id) {
    await wait(180);
    const resource = infrastructureResources.find((item) => item.id === id);
    if (!resource) throw new Error("基础设施资源不存在或已被移除");
    return { resource, relations: resourceRelations.filter((item) => (item.sourceKind === "infrastructure" && item.sourceId === id) || (item.targetKind === "infrastructure" && item.targetId === id)) };
  },
  async listResourceRelations(kind, resourceId) {
    await wait(160);
    if (!kind || !resourceId) return resourceRelations;
    return resourceRelations.filter((item) => (item.sourceKind === kind && item.sourceId === resourceId) || (item.targetKind === kind && item.targetId === resourceId));
  },
  async acknowledgeAlert(id, note) {
    await wait(380);
    const alert = alerts.find((item) => item.id === id);
    if (!alert) throw new Error("告警不存在");
    alert.status = "acknowledged";
    alert.assignee = "陈立";
    alert.description = `${alert.description} 备注：${note}`;
    return alert;
  },
  async executeInstanceAction(instanceId, action, reason) {
    await wait(680);
    const instance = instances.find((item) => item.id === instanceId);
    if (!instance) throw new Error("目标实例不存在");
    const service = services.find((item) => item.id === instance.serviceId);
    const actionName = { restart: "实例重启", online: "实例上线", offline: "实例下线" }[action];
    const audit: OperationAudit = {
      id: `AUD-${Date.now()}`,
      operator: "陈立",
      role: "系统管理员",
      action: actionName,
      resourceType: "ServiceInstance",
      resourceName: instance.name,
      parameters: `reason=${reason}; mode=mock`,
      result: "success",
      environment: service?.environment ?? "production",
      ip: "127.0.0.1",
      createdAt: new Date().toLocaleString("zh-CN", { hour12: false }),
      requestId: `req-${Math.random().toString(16).slice(2, 8)}`,
    };
    audits.unshift(audit);
    return audit;
  },
};

export const topologyApi = {
  async getTopology() {
    await wait(180);
    return { services, dependencies };
  },
};

// External adapter contracts to implement in production:
// DiscoveryAdapter -> Nacos / Kubernetes / Consul
// MetricsAdapter   -> Prometheus
// LogsAdapter      -> Loki / Elasticsearch
// MiddlewareInventoryAdapter -> PostgreSQL / Redis / Kafka / Elasticsearch / Nacos / MinIO (read-only in this phase)
// InfrastructureInventoryAdapter -> Kubernetes / node exporter / CMDB (read-only in this phase)
// OperationAdapter -> Kubernetes / deployment platform (disabled by default)
export const opsApi: OpsApi = mockOpsApi;
